import {
    ClassCompetency,
    ClassCompetencyContext,
    ClassCompetencyData,
    ClassCompetencyDataOverallStats,
    ClassCompetencyDataDrilldown,
    ClassProgressReportModel,
    ReportStudentListModel,
} from '@/models/groups/class';
import { sessionService } from '@/providers/services/auth/session';
import { DEFAULT_IMAGES_PATH, DIAGNOSTIC_STATUS } from '@/utils/constants';
import {
    ClassTimespentContext,
    ClassTimespentData,
    ClassTimespent,
    ClassTimespentOverallStats,
    ClassTimespentDrilldown,
} from '@/models/groups/timespent';

class ClassGroupSerialize {
    public static instance = new ClassGroupSerialize();

    private session: any = sessionService.getSession();

    public serializeClassCompetency(payload: any): ClassCompetency {
        const serializedClassCompeteny: ClassCompetency | any = {};
        if (payload) {
            if (payload.context) {
                const serializedClassCompetenyContext: ClassCompetencyContext = {
                    report: payload.context.report,
                    hierarchy: payload.context.hierarchy,
                    tenants: payload.context.tenants,
                    groupId: payload.context.group_id,
                    groupType: payload.context.group_type,
                    week: payload.context.week,
                    month: payload.context.month,
                    year: payload.context.year,
                    duration: payload.context.duration,
                    frequencey: payload.context.frequencey,
                    subject: payload.context.subject || null,
                    framework: payload.context.framework || null,
                    categoryId: payload.context.subject_classification_id || null,
                };
                serializedClassCompeteny.context = serializedClassCompetenyContext;
            }
            if (payload.data) {
                serializedClassCompeteny.data = payload.data.map((payloadData: any) => {
                    const serializedClassCompetencyData: ClassCompetencyData = {
                        id: payloadData.id,
                        name: payloadData.name,
                        code: payloadData.code,
                        type: payloadData.type,
                        overallStats: this.normalizeClassCompetencyOverallStats(payloadData.overall_stats),
                        drilldown: this.normalizeClassCompetencyDrilldown(payloadData.drilldown),
                    };
                    return serializedClassCompetencyData;
                });

            }
        }
        return serializedClassCompeteny;
    }

    public serializeClassTimespent(payload: any) {
        const serializedGroupsTimespent: ClassTimespent | any = {};
        if (payload) {
            if (payload.context) {
                const timespentontext: ClassTimespentContext = {
                    report: payload.context.report,
                    hierarchy: payload.context.hierarchy,
                    tenants: payload.context.tenants,
                    groupId: payload.context.group_id,
                    groupType: payload.context.group_type,
                    week: payload.context.week,
                    month: payload.context.month,
                    year: payload.context.year,
                    duration: payload.context.duration,
                    frequency: payload.context.frequency,
                };
                serializedGroupsTimespent.context = timespentontext;
            }

            if (payload.data) {
                serializedGroupsTimespent.data = payload.data.map((payloadData: any) => {
                    const timespentData: ClassTimespentData = {
                        id: payloadData.id,
                        name: payloadData.name,
                        code: payloadData.code,
                        type: payloadData.type,
                        overallStats: this.normalizeClassTimespentOverallStats(payloadData.overall_stats),
                        coordinates: [],
                        drilldown: this.normalizeClassTimespentDrilldown(payloadData.drilldown),
                    };
                    return timespentData;
                });
            }
        }
        return serializedGroupsTimespent;
    }

    public serializeClassProgressReport(payload: any): ClassProgressReportModel {
        const studentPayload: any = payload.students ? payload.students : [];
        const teacherPayload: any = payload.teacher ? payload.teacher : null;
        const studentsSummary: ReportStudentListModel[] = [];
        studentPayload.forEach((student: any) => {
            const interactions = student.summaryData.interactions;
            const suggestions = student.summaryData.suggestions ? student.summaryData.suggestions : null;
            const collection = interactions && interactions.collection ? interactions.collection : null;
            const assessment = interactions && interactions.assessment ? interactions.assessment : null;
            const collectionTimespent = collection ? collection.totalTimespent : 0;
            const assessmentTimespent = assessment ? assessment.totalTimespent : 0;
            const totalTimespent = collectionTimespent + assessmentTimespent;
            const collectionSuggestion = suggestions && suggestions.collection ? suggestions.collection.count : 0;
            const assessmentSuggestion = suggestions && suggestions.assessment ? suggestions.assessment.count : 0;
            const diagnosticStatus: any = DIAGNOSTIC_STATUS;
            const summaryData = student.summaryData;
            const diagnosticMastery = this.extractDiagnosticsMastery(summaryData.completed);
            const masteryCount = summaryData ?
                 (((summaryData.completed || []).length - diagnosticMastery.length)
                     + ((summaryData.mastered || []).length)) : 0;
            const studentDetails: ReportStudentListModel = {
                firstName: student.student.firstName,
                lastName: student.student.lastName,
                email: student.student.email,
                id: student.student.id,
                collectionTimespent,
                assessmentTimespent,
                totalTimespent,
                suggestionTaken: collectionSuggestion + assessmentSuggestion,
                averageScore: assessment ? assessment.averageScore : 0,
                badgeEarned: student.summaryData.mastered.length,
                profileImage: student.student.profileImage ? student.student.profileImage : DEFAULT_IMAGES_PATH.profile,
                mastered: student.summaryData.mastered,
                completed: student.summaryData.completed,
                inferred: student.summaryData.inferred,
                masteryCount,
                inprogress: student.summaryData.inprogress,
                reinforcedMastery: student.summaryData.reinforcedMastery ? student.summaryData.reinforcedMastery : [],
                diagnosticAssessmentStatus: diagnosticStatus[student.summaryData.diagnosticAssessmentStatus],
                interactions: student.summaryData.interactions,
                diagnosticsMastery: diagnosticMastery,
                startDate: student.summaryData.startDate,
                endDate: student.summaryData.endDate,
                suggestions: student.summaryData.suggestions,
            };
            studentsSummary.push(studentDetails);
        });
        return {
            students: studentsSummary,
            teacher: teacherPayload,
        };
    }

    private extractDiagnosticsMastery(list: any) {
        let diagnostic: any = [];
        if (list && list.length > 0) {
            diagnostic = list.filter((item: any) => {
                return item.content_source.indexOf('diagnostic') !== -1;
            });
        }
        return diagnostic;
    }


    private normalizeClassCompetencyOverallStats(overallStats: any): ClassCompetencyDataOverallStats {
        return {
            totalCompletedCompetencies: overallStats.total_completed_competencies || 0,
            totalInferredCompetencies: overallStats.total_inferred_competencies || 0,
            totalInprogressCompetencies: overallStats.total_inprogress_competencies || 0,
            totalNotstartedCompetencies: overallStats.total_notstarted_competencies || 0,
            overallAveragePerformance: overallStats.overall_average_performance === null ?
                -1 :
                overallStats.overall_average_performance,
            overallMasteredCompetencies: (overallStats.total_completed_competencies || 0)
                + (overallStats.total_inferred_competencies || 0) +
                (overallStats.total_diagnostic_completed_competencies || 0) +
                (overallStats.total_diagnostic_inferred_competencies || 0),
            totalSkippedInferredComptencies: overallStats.total_skipped_inferred_competencies || 0,
            totalCompetencies: overallStats ?
                (overallStats.total_completed_competencies || 0) +
                (overallStats.total_inferred_competencies || 0) +
                (overallStats.total_inprogress_competencies || 0) +
                (overallStats.total_notstarted_competencies || 0) +
                (overallStats.total_diagnostic_completed_competencies || 0) +
                (overallStats.total_diagnostic_inferred_competencies || 0) : 0,
            totalDiagnosticCompetencies: overallStats ? overallStats.total_diagnostic_completed_competencies : 0,
            totalDiagnosticInferredCompetencies: overallStats ? overallStats.total_diagnostic_inferred_competencies : 0,
        };
    }

    private normalizeClassCompetencyDrilldown(drilldown: any[]): ClassCompetencyDataDrilldown[] {
        return drilldown.map((drilldownData: any, seq: number) => {
            const completedCompetencies = drilldownData.completed_competencies || 0;
            const inferredCompetencies = drilldownData.inferred_competencies || 0;
            const inprogressCompetencies = drilldownData.inprogress_competencies || 0;
            const nostartedCompetencies = drilldownData.notstarted_competencies || 0;
            const skippedInferredComptencies = drilldownData.skipped_inferred_competencies || 0;
            const diagnosticCompetencies = drilldownData.diagnostic_completed_competencies || 0;
            const diagnosticInferredCompetencies = drilldownData.diagnostic_inferred_competencies || 0;
            const normalizedData: ClassCompetencyDataDrilldown = {
                id: drilldownData.id,
                firstName: drilldownData.first_name,
                lastName: drilldownData.last_name,
                fullName: `${drilldownData.last_name}, ${drilldownData.first_name}`,
                email: drilldownData.email,
                thumbnailUrl: drilldownData.thumbnail
                    ? this.session.cdn_urls.user_cdn_url + drilldownData.thumbnail
                    : DEFAULT_IMAGES_PATH.profile,
                completedCompetencies,
                inferredCompetencies,
                inprogressCompetencies,
                nostartedCompetencies,
                totalCompetencies: completedCompetencies
                    + inferredCompetencies
                    + inprogressCompetencies
                    + nostartedCompetencies,
                skippedInferredComptencies,
                masteredCompetencies: completedCompetencies + inferredCompetencies +
                    diagnosticCompetencies + diagnosticInferredCompetencies,
                averagePerformance: drilldownData.average_performance === null ? -1 : drilldownData.average_performance,
                name: `${drilldownData.last_name}, ${drilldownData.first_name}`,
                type: 'student',
                seq: seq + 1,
                diagnosticCompetencies,
                diagnosticInferredCompetencies,
            };
            return normalizedData;
        });
    }

    private normalizeClassTimespentOverallStats(overallStats: any) {
        const normalizedClassTsOverallStats: ClassTimespentOverallStats = {
            activeStudents: overallStats ? (overallStats.active_students || 0) : 0,
            activeStudentsIndex: overallStats ? (overallStats.active_students_index || 0) : 0,
            totalStudents: overallStats ? (overallStats.total_students || 0) : 0,
            totalCollectionTimespent: overallStats ? (overallStats.total_collection_timespent || 0) : 0,
            totalAssessmentTimespent: overallStats ? (overallStats.total_assessment_timespent || 0) : 0,
            totalOaTimespent: overallStats ? (overallStats.total_oa_timespent || 0) : 0,
            averageAssessmentTimespent: overallStats ? (overallStats.average_assessment_timespent || 0) : 0,
            averageCollectionTimespent: overallStats ? (overallStats.average_collection_timespent || 0) : 0,
            averageOaTimespent: overallStats ? (overallStats.average_oa_timespent || 0) : 0,
            overallAverageTimespent: overallStats ?
                (overallStats.overall_average_timespent || 0) :
                0,
        };
        return normalizedClassTsOverallStats;
    }

    private normalizeClassTimespentDrilldown(drilldown: any[]): ClassTimespentDrilldown[] {
        return drilldown.map((drilldownData: any) => {
            const normalizedClassTsDrilldown: ClassTimespentDrilldown = {
                id: drilldownData.id,
                name: `${drilldownData.last_name}, ${drilldownData.first_name}`,
                firstName: drilldownData.first_name,
                lastName: drilldownData.last_name,
                email: drilldownData.email,
                thumbnail: drilldownData.thumbnail
                    ? this.session.cdn_urls.user_cdn_url + drilldownData.thumbnail
                    : DEFAULT_IMAGES_PATH.profile,
                averageAssessmentTimespent: drilldownData.average_assessment_timespent || 0,
                averageCollectionTimespent: drilldownData.average_collection_timespent || 0,
                averageOaTimespent: drilldownData.average_oa_timespent || 0,
                overallAverageTimespent: drilldownData.overall_average_timespent || 0,
                totalAssessmentTimespent: drilldownData.total_assessment_timespent || 0,
                totalCollectionTimespent: drilldownData.total_collection_timespent || 0,
                totalOaTimespent: drilldownData.total_oa_timespent || 0,
            };
            return normalizedClassTsDrilldown;
        });
    }
}

export const classGroupSerializer = ClassGroupSerialize.instance;
